import { useEffect, useRef } from 'react';
import { useUser } from '../UserContext';
import { useConnection } from '../ConnectionContext';
import nativeWebSocket from '../websocket-native';

/**
 * Hook para manejar reconexión automática del WebSocket
 * cuando se pierde la conexión o cuando vuelve internet
 */
const useAutoReconnect = () => {
  const { user } = useUser();
  const { isOnline } = useConnection();
  const reconnectTimeoutRef = useRef(null);
  const lastOnlineStateRef = useRef(isOnline);

  useEffect(() => {
    // Detectar cuando vuelve la conexión a internet
    if (!lastOnlineStateRef.current && isOnline) {
      console.log('🌐 Conexión a Internet restaurada');
      
      // Si hay usuario y el WebSocket no está conectado, reconectar
      if (user && user.id && nativeWebSocket.getConnectionState() !== 'connected') {
        // Cancelar cualquier timeout de reconexión pendiente
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }

        // Esperar un poco antes de reconectar para dar tiempo a que la red se estabilice
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('🔌 Intentando reconectar WebSocket después de recuperar Internet...');
          nativeWebSocket.connect();
        }, 2000);
      }
    }

    // Actualizar el estado anterior
    lastOnlineStateRef.current = isOnline;

    // Cleanup
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [isOnline, user]);

  // Manejar reconexión cuando el WebSocket se desconecta inesperadamente
  useEffect(() => {
    let isSubscribed = true;
    
    const handleWebSocketError = (state) => {
      if (!isSubscribed) return;
      
      if (state === 'error' || state === 'disconnected') {
        // Solo intentar reconectar si hay internet y usuario autenticado
        if (isOnline && user && user.id) {
          const currentState = nativeWebSocket.getConnectionState();
          
          // Evitar reconexión si ya está conectando o conectado
          if (currentState === 'connecting' || currentState === 'connected') {
            return;
          }
          
          console.log('⚠️ WebSocket desconectado, programando reconexión...');
          
          // Cancelar cualquier timeout pendiente
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }

          // Esperar antes de reconectar
          reconnectTimeoutRef.current = setTimeout(() => {
            if (!isSubscribed) return;
            
            const state = nativeWebSocket.getConnectionState();
            if (state !== 'connected' && state !== 'connecting') {
              console.log('🔄 Intentando reconectar WebSocket...');
              nativeWebSocket.connect();
            }
          }, 5000);
        }
      }
    };

    // Suscribirse a cambios de estado del WebSocket
    nativeWebSocket.onStatusChange(handleWebSocketError);

    // Cleanup
    return () => {
      isSubscribed = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [isOnline, user]);

  // Verificar el estado del WebSocket periódicamente
  useEffect(() => {
    const checkInterval = setInterval(() => {
      if (user && user.id && isOnline) {
        const state = nativeWebSocket.getConnectionState();
        if (state !== 'connected' && state !== 'connecting') {
          console.log('🔍 Verificación periódica: WebSocket no conectado, intentando reconectar...');
          nativeWebSocket.connect();
        }
      }
    }, 30000); // Cada 30 segundos

    return () => clearInterval(checkInterval);
  }, [user, isOnline]);
};

export default useAutoReconnect;